// Saldo del vendedor en el libro de pagos: lo ganado, lo depositado, lo pendiente y sus depósitos.
import type { RetailCommission } from '@fiado/type-kit';
import { Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { StatusPill } from '@/shared/patterns/Pills';
import { isLedgerMissing, useSellerLedger } from '../../api/commissionQueries';
import {
    attentionText,
    PAYMENT_STATUS_LABEL,
    PAYMENT_STATUS_TONE,
    recentPayments,
} from '../../lib/paymentRows';
import { SECTION_TITLE } from '../fieldStyles';

const RECENT = 5;
const FIGURE_LABEL = 'text-[9.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground';
const FIGURE = 'text-[15px] font-semibold tabular-nums';

function LedgerBody({ ledger }: { ledger: RetailCommission.GetSellerLedgerResponse }) {
    const { balance } = ledger;
    const owes = balance.payableCents < 0;
    const extra = [
        balance.inTransitCents > 0
            ? `En tránsito ${formatMoneyCents(balance.inTransitCents)}`
            : null,
        balance.retainedCents > 0
            ? `Retenido para fin de mes ${formatMoneyCents(balance.retainedCents)}`
            : null,
        balance.paidOutsideCents > 0
            ? `Pagado por fuera ${formatMoneyCents(balance.paidOutsideCents)}`
            : null,
    ].filter((text) => text !== null);
    const payments = recentPayments(ledger.payments, RECENT);
    return (
        <>
            <dl className="grid grid-cols-3 gap-2" aria-label="Saldo del vendedor">
                <div>
                    <dt className={FIGURE_LABEL}>Ganado</dt>
                    <dd className={FIGURE}>{formatMoneyCents(balance.earnedCents)}</dd>
                </div>
                <div>
                    <dt className={FIGURE_LABEL}>Depositado</dt>
                    <dd className={FIGURE}>{formatMoneyCents(balance.accreditedCents)}</dd>
                </div>
                <div>
                    <dt className={FIGURE_LABEL}>{owes ? 'Debe' : 'Pendiente'}</dt>
                    <dd className={cn(FIGURE, owes && 'text-danger')}>
                        {formatMoneyCents(Math.abs(balance.payableCents))}
                    </dd>
                </div>
            </dl>
            {extra.length > 0 && (
                <p className="mt-1 text-[10.5px] text-muted-foreground">{extra.join(' · ')}</p>
            )}
            {balance.attention !== null && (
                <p className="mt-1.5">
                    <StatusPill tone={owes ? 'danger' : 'warning'}>
                        {attentionText(balance)}
                    </StatusPill>
                </p>
            )}
            {payments.length > 0 && (
                <ul aria-label="Depósitos recientes" className="mt-2 flex flex-col gap-1">
                    {payments.map((p) => (
                        <li key={p.paymentId} className="flex items-center gap-2 text-[11px]">
                            <span className="w-[86px] flex-none text-muted-foreground">
                                {formatDateMx(p.requestedAt)}
                            </span>
                            <span className="flex-1 tabular-nums">
                                {formatMoneyCents(p.amountCents)}
                            </span>
                            <StatusPill tone={PAYMENT_STATUS_TONE[p.status]}>
                                {PAYMENT_STATUS_LABEL[p.status]}
                            </StatusPill>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}

export function SellerPaymentsSection({ sellerId }: { sellerId: string }) {
    const ledger = useSellerLedger(sellerId);
    return (
        <section className="border-b border-border px-4 py-3" aria-label="Pagos a la wallet">
            <h3 className={SECTION_TITLE}>Pagos a la wallet</h3>
            {ledger.isLoading ? (
                <Skeleton lines={2} />
            ) : isLedgerMissing(ledger.error) ? (
                <p className="text-[11px] text-muted-foreground">Todavía no tiene pagos.</p>
            ) : ledger.error ? (
                <p className="text-[11px] text-destructive">No se pudo leer su saldo.</p>
            ) : ledger.data ? (
                <LedgerBody ledger={ledger.data} />
            ) : null}
        </section>
    );
}
